"use client";
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import { useRouter, useSearchParams } from "next/navigation";

interface MemeCoinsPaginationProps {
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

export default function MemeCoinsPagination({
  pagination,
}: MemeCoinsPaginationProps) {
  const { page, totalPages, hasNextPage, hasPrevPage } = pagination;
  const router = useRouter();
  const searchParams = useSearchParams();

  const createQueryString = (name: string, value: string) => {
    const params = new URLSearchParams(searchParams.toString());
    params.set(name, value);
    return params.toString();
  };

  const handlePageChange = (newPage: number) => {
    router.push(`?${createQueryString("page", newPage.toString())}`);
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handlePrevPage = () => {
    if (hasPrevPage) {
      handlePageChange(page - 1);
    }
  };

  const handleNextPage = () => {
    if (hasNextPage) {
      handlePageChange(page + 1);
    }
  };

  const renderPageNumbers = () => {
    const maxVisiblePages = 5;

    let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    return Array.from(
      { length: endPage - startPage + 1 },
      (_, index) => startPage + index
    ).map((i) => (
      <button
        key={i}
        onClick={() => handlePageChange(i)}
        className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full border border-primaryborder transition-all duration-200 cursor-pointer ${
          i === page
            ? "bg-blue-500 text-white border-blue-500 shadow-[0_0_12px_#007AFF]"
            : "bg-gradient-button-dark text-gray-300 hover:bg-opacity-80"
        }`}
      >
        {i}
      </button>
    ));
  };

  return (
    <div className="my-4">
      <div className="flex items-center justify-center space-x-2">
        <button
          className={`flex items-center justify-center w-10 h-10 rounded-full cursor-pointer ${
            !hasPrevPage
              ? "bg-gradient-button-dark text-gray-500 cursor-not-allowed"
              : "bg-gradient-button-dark text-white hover:bg-opacity-80"
          } border border-primaryborder transition-all duration-200`}
          onClick={handlePrevPage}
          disabled={!hasPrevPage}
        >
          <IconChevronLeft size={20} />
        </button>

        {renderPageNumbers()}

        <button
          className={`flex items-center justify-center w-10 h-10 rounded-full cursor-pointer ${
            !hasNextPage
              ? "bg-gradient-button-dark text-gray-500 cursor-not-allowed"
              : "bg-gradient-button-dark text-white hover:bg-opacity-80"
          } border border-primaryborder transition-all duration-200`}
          onClick={handleNextPage}
          disabled={!hasNextPage}
        >
          <IconChevronRight size={20} />
        </button>
      </div>
    </div>
  );
}
